import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export type QualificationData = {
  primaryService: string;
  areaStatus: string;
  otherServices: string[];
  otherServicesText: string;
  town: string;
  county: string;
  vanCount: number;
  customerCount: number;
  yearlyRoundValue: string;
  currentMarketing: string[];
  agencyHistory: string;
  currentAdSpend: string;
  futureAdBudget: string;
  twelveMonthGoal: string;
};

type Props = {
  onComplete: (
    data: QualificationData,
    score: string,
    softFlags: string[],
  ) => void;
};

const WAITLIST_WEBHOOK_URL =
  "https://services.leadconnectorhq.com/hooks/nsp8u9BgTJB40BULOWfw/webhook-trigger/bbe386c0-ad3a-4ae2-b91b-3c50364f008a";
const NURTURE_WEBHOOK_URL =
  "https://services.leadconnectorhq.com/hooks/nsp8u9BgTJB40BULOWfw/webhook-trigger/bbe386c0-ad3a-4ae2-b91b-3c50364f008a";

const generateRoundValueOptions = () => {
  const options = ["Under £30,000"];
  for (let i = 30000; i < 100000; i += 10000) {
    options.push(
      `£${i.toLocaleString("en-GB")} – £${(i + 10000).toLocaleString("en-GB")}`,
    );
  }
  for (let i = 100000; i < 1000000; i += 25000) {
    options.push(
      `£${i.toLocaleString("en-GB")} – £${(i + 25000).toLocaleString("en-GB")}`,
    );
  }
  options.push("£1,000,000+");
  return options;
};
const ROUND_VALUE_OPTIONS = generateRoundValueOptions();

export const PreQualForm = ({ onComplete }: Props) => {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [hasReachedSummary, setHasReachedSummary] = useState(false);
  const [exitReason, setExitReason] = useState<
    "service" | "area_taken" | "budget" | null
  >(null);

  const [data, setData] = useState<QualificationData>(() => {
    const saved = localStorage.getItem("cm_qual_data");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        // ignore
      }
    }
    return {
      primaryService: "",
      areaStatus: "",
      otherServices: [],
      otherServicesText: "",
      town: "",
      county: "",
      vanCount: 1,
      customerCount: 0,
      yearlyRoundValue: "",
      currentMarketing: [],
      agencyHistory: "",
      currentAdSpend: "",
      futureAdBudget: "",
      twelveMonthGoal: "",
    };
  });

  useEffect(() => {
    localStorage.setItem("cm_qual_data", JSON.stringify(data));
  }, [data]);

  const updateData = (updates: Partial<QualificationData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  };

  const nextStep = () => {
    if (step === 0) {
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!data.primaryService) return;
      if (data.primaryService === "No") {
        setExitReason("service");
        return;
      }
    }
    if (step === 2) {
      if (!data.areaStatus) return;
      if (data.areaStatus === "Taken") {
        setExitReason("area_taken");
        return;
      }
      if (data.areaStatus === "Haven't checked") {
        toast({
          title: "Please check the map",
          description:
            "Please check the map first using the link above, then come back and select whether your area is available.",
          variant: "destructive",
        });
        return;
      }
    }
    if (step === 4) {
      if (!data.town || !data.county) {
        toast({
          title: "Missing fields",
          description: "Please enter both your town and county.",
          variant: "destructive",
        });
        return;
      }
    }
    if (step === 7) {
      if (!data.yearlyRoundValue) return;
    }
    if (step === 9) {
      if (!data.agencyHistory) return;
    }
    if (step === 10) {
      if (!data.currentAdSpend) return;
    }
    if (step === 11) {
      if (!data.futureAdBudget) return;
      if (data.futureAdBudget === "Under £1,000") {
        setExitReason("budget");
        return;
      }
    }
    if (step === 12) {
      if (!data.twelveMonthGoal) return;
    }

    if (hasReachedSummary && step !== 0) {
      setStep(13);
      return;
    }

    setStep((prev) => {
      const next = prev + 1;
      if (next === 13) setHasReachedSummary(true);
      try {
        if (typeof window !== "undefined" && (window as any).dataLayer) {
          (window as any).dataLayer.push({
            event: "qualification_step_view",
            step: next,
          });
        }
      } catch (e) {}
      return next;
    });
  };

  const prevStep = () => setStep((prev) => Math.max(1, prev - 1));

  const handleComplete = () => {
    // Calculate flags and score
    const softFlags: string[] = [];
    if (data.primaryService === "Alongside")
      softFlags.push("alongside_other_services");
    if (data.customerCount < 100) softFlags.push("early_stage");

    // Yearly round value per customer calculation
    let val = 0;
    if (data.yearlyRoundValue === "Under £30,000") {
      val = 15000;
    } else if (data.yearlyRoundValue === "£1,000,000+") {
      val = 1000000;
    } else if (data.yearlyRoundValue) {
      const parts = data.yearlyRoundValue.replace(/£|,/g, "").split(" – ");
      if (parts.length === 2) {
        val = (parseInt(parts[0], 10) + parseInt(parts[1], 10)) / 2;
      }
    }
    if (data.customerCount > 0 && val > 0) {
      if (val / data.customerCount < 100) {
        softFlags.push("pricing_review_needed");
      }
    }

    const isFirstTime = [
      "Nothing currently",
      "Under £250",
      "£100",
      "£200",
    ].includes(data.currentAdSpend);
    if (isFirstTime) {
      softFlags.push("first_time_advertiser");
    }

    if (
      data.twelveMonthGoal === "Steady — replace churn, no major growth plans"
    ) {
      softFlags.push("low_growth_intent");
    }

    let score = "good_fit";
    if (
      softFlags.length === 0 &&
      data.customerCount >= 300 &&
      data.futureAdBudget !== "Under £1,000"
    ) {
      score = "strong_fit";
    } else if (softFlags.length >= 2) {
      score = "needs_review";
    }

    try {
      if (typeof window !== "undefined" && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: "qualification_complete",
          score: score,
        });
        (window as any).dataLayer.push({
          event: "qualification_to_booking",
          score: score,
        });
      }
    } catch (e) {}

    // Pass data
    onComplete(data, score, softFlags);
  };

  // Waitlist/Nurture form state
  const [contactForm, setContactForm] = useState({
    name: "",
    businessName: "",
    email: "",
    town: "",
  });
  const [submittingContact, setSubmittingContact] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);

  const submitExitForm = async (type: "waitlist" | "nurture") => {
    if (!contactForm.email) return;
    setSubmittingContact(true);
    try {
      const url =
        type === "waitlist" ? WAITLIST_WEBHOOK_URL : NURTURE_WEBHOOK_URL;
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...contactForm,
          type,
          tags:
            type === "waitlist"
              ? ["waitlist_area_taken"]
              : ["budget_too_low_nurture"],
        }),
      });

      const trackingPayload = {
        type: "external_form_submission",
        timestamp: Date.now(),
        formId: type === "waitlist" ? "Waitlist Form" : "Nurture Form",
        formData: {
          first_name: contactForm.name,
          email: contactForm.email,
          organization: contactForm.businessName,
          city: contactForm.town,
        },
        formLabels: {
          first_name: "Name",
          email: "Email",
          organization: "Business Name",
          city: "Town",
        },
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        trackingId: "tk_4228c869be4b43a6af25f3931923e54d",
        locationId: "nsp8u9BgTJB40BULOWfw",
        sessionId: crypto.randomUUID(),
        properties: {
          deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
            ? "mobile"
            : "desktop",
        },
      };

      fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
        method: "POST",
        headers: { "Content-Type": "application/json", version: "2021-07-28" },
        body: JSON.stringify(trackingPayload),
      }).catch(() => {});

      setContactSubmitted(true);
    } catch (e) {
      toast({
        title: "Error submitting",
        description:
          "There was a problem submitting your details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmittingContact(false);
    }
  };

  useEffect(() => {
    if (exitReason) {
      try {
        if (typeof window !== "undefined" && (window as any).dataLayer) {
          (window as any).dataLayer.push({
            event: "qualification_exit",
            reason: exitReason,
          });
        }
      } catch (e) {}
    }
  }, [exitReason]);

  if (exitReason) {
    return (
      <div className="max-w-2xl mx-auto py-2 animate-fade-in">
        <div className="bg-white text-center p-6 sm:p-10">
          <div className="flex justify-center mb-6">
            <img
              src="https://vibe.filesafe.space/1775166810156919871/attachments/07b67583-88fd-417b-bb44-aeaaee1d2dda.png"
              alt="Clean Marketing"
              className="h-12"
            />
          </div>

          {exitReason === "service" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-secondary">
                Thanks for your interest.
              </h2>
              <p className="text-slate-600 text-lg">
                Clean Marketing is built specifically for businesses where
                window cleaning is the primary service. Our systems, pricing,
                and strategies are tailored exclusively for window cleaners,
                which means we may not be the right fit for your business right
                now.
              </p>
              <p className="text-slate-600 font-medium">
                We wish you the best of luck with your business.
              </p>
            </div>
          )}

          {exitReason === "area_taken" && (
            <div className="space-y-6 text-left">
              <h2 className="text-2xl font-bold text-secondary text-center">
                Area Unavailable
              </h2>
              <p className="text-slate-600 text-lg text-center">
                Thanks for checking. Because we work on an exclusivity basis,
                we're unable to take on a second business in your area while we
                have an existing partner there.
              </p>

              {!contactSubmitted ? (
                <div className="mt-8 bg-slate-50 p-6 rounded-xl border border-slate-200">
                  <h3 className="font-bold text-secondary mb-4">
                    Join the Waitlist
                  </h3>
                  <p className="text-sm text-slate-600 mb-6">
                    If you'd like to be added to our waitlist in case the area
                    becomes available, please leave your details below.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={contactForm.name}
                        onChange={(e) =>
                          setContactForm((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>Business Name</Label>
                      <Input
                        value={contactForm.businessName}
                        onChange={(e) =>
                          setContactForm((prev) => ({
                            ...prev,
                            businessName: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) =>
                          setContactForm((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>Town</Label>
                      <Input
                        value={contactForm.town}
                        onChange={(e) =>
                          setContactForm((prev) => ({
                            ...prev,
                            town: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={() => submitExitForm("waitlist")}
                      disabled={submittingContact || !contactForm.email}
                    >
                      {submittingContact ? "Submitting..." : "Join Waitlist"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-8 bg-green-50 p-6 rounded-xl border border-green-200 text-center">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="font-bold text-green-800 mb-2">
                    Added to Waitlist
                  </h3>
                  <p className="text-green-700">
                    We'll be in touch if your area becomes available.
                  </p>
                </div>
              )}
            </div>
          )}

          {exitReason === "budget" && (
            <div className="space-y-6 text-left">
              <h2 className="text-2xl font-bold text-secondary text-center">
                Thanks for your honesty.
              </h2>
              <p className="text-slate-600 text-lg text-center">
                Because our system is built around a minimum £1,000/month ad
                spend, we wouldn't be able to deliver the kind of results you'd
                want to see at a lower budget — and we'd rather tell you that
                now than waste your time on a demo.
              </p>
              <p className="text-slate-600 text-center">
                When you're ready to invest at that level, we'd love to talk.
              </p>

              {!contactSubmitted ? (
                <div className="mt-8 bg-slate-50 p-6 rounded-xl border border-slate-200">
                  <h3 className="font-bold text-secondary mb-4">
                    Free Resources
                  </h3>
                  <p className="text-sm text-slate-600 mb-6">
                    Want free advice and resources for growing your window
                    cleaning business? Drop your email below and we'll send our
                    best content your way.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={contactForm.name}
                        onChange={(e) =>
                          setContactForm((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) =>
                          setContactForm((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={() => submitExitForm("nurture")}
                      disabled={submittingContact || !contactForm.email}
                    >
                      {submittingContact
                        ? "Submitting..."
                        : "Get Free Resources"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-8 bg-green-50 p-6 rounded-xl border border-green-200 text-center">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="font-bold text-green-800 mb-2">
                    You're on the list!
                  </h3>
                  <p className="text-green-700">
                    Keep an eye on your inbox for our latest resources.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-slate-100 text-left">
            <details className="group">
              <summary className="text-sm text-slate-500 cursor-pointer font-medium hover:text-primary list-none flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                Why this requirement?
              </summary>
              <div className="mt-3 text-xs text-slate-500 pl-6 border-l-2 border-slate-100">
                {exitReason === "service" &&
                  "Our entire system—from ad copy and targeting to automated quoting logic—is built specifically for window cleaning. Applying it to other services yields poor results."}
                {exitReason === "area_taken" &&
                  "We guarantee exclusivity to our clients. If we run ads for two window cleaners in the same town, we end up bidding against ourselves and driving up costs for everyone."}
                {exitReason === "budget" &&
                  "Digital marketing requires sufficient data for algorithms to optimize. Below £1,000/mo, campaigns struggle to exit the learning phase, resulting in high costs and poor lead quality."}
              </div>
            </details>
          </div>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6 animate-fade-in text-center py-8">
            <div className="flex justify-center mb-6">
              <img
                src="https://vibe.filesafe.space/1775166810156919871/attachments/07b67583-88fd-417b-bb44-aeaaee1d2dda.png"
                alt="Clean Marketing"
                className="h-12"
              />
            </div>
            <h2 className="text-3xl font-bold text-secondary">Book a Demo</h2>
            <p className="text-lg text-slate-600 max-w-lg mx-auto">
              To make sure we're a great fit and can deliver the results you're
              looking for, please complete this quick qualification form before
              booking your demo.
            </p>
            <div className="pt-6">
              <Button
                onClick={() => setStep(1)}
                size="lg"
                className="bg-primary text-white hover:bg-[#e04825] text-lg px-10 h-14 w-full sm:w-auto"
              >
                Start Now <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              Is window cleaning the primary service in your business?
            </h2>
            <div className="space-y-3">
              {[
                {
                  label: "Yes, window cleaning is our main service",
                  val: "Yes",
                },
                {
                  label:
                    "We offer window cleaning alongside other services equally",
                  val: "Alongside",
                },
                {
                  label: "No, window cleaning isn't our main service",
                  val: "No",
                },
              ].map((opt) => (
                <Button
                  key={opt.val}
                  variant="outline"
                  className={`w-full justify-start h-auto py-5 px-6 text-left whitespace-normal text-base font-medium ${data.primaryService === opt.val ? "border-primary bg-primary/5 ring-1 ring-primary text-secondary" : "text-slate-700 hover:border-primary/50"}`}
                  onClick={() => updateData({ primaryService: opt.val })}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              Is your area available?
            </h2>
            <p className="text-slate-600 text-lg">
              We work with one window cleaning business per area on an
              exclusivity basis. Have you checked our service area map to
              confirm your area is available?
            </p>
            <a
              href="/#coverage"
              className="inline-flex items-center justify-center w-full bg-slate-100 hover:bg-slate-200 text-secondary font-medium py-3 px-4 rounded-lg transition-colors border border-slate-200 mb-2"
            >
              <MapPin className="w-5 h-5 mr-2 text-primary" />
              Check the service area checker
            </a>
            <div className="space-y-3">
              {[
                { label: "Yes, my area is available", val: "Yes" },
                {
                  label: "I've checked, but my area is already taken",
                  val: "Taken",
                },
                { label: "I haven't checked yet", val: "Haven't checked" },
              ].map((opt) => (
                <Button
                  key={opt.val}
                  variant="outline"
                  className={`w-full justify-start h-auto py-5 px-6 text-left whitespace-normal text-base font-medium ${data.areaStatus === opt.val ? "border-primary bg-primary/5 ring-1 ring-primary text-secondary" : "text-slate-700 hover:border-primary/50"}`}
                  onClick={() => updateData({ areaStatus: opt.val })}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
        );
      case 3:
        const services = [
          "Gutter clearance",
          "Gutter, fascia & soffit washing",
          "Conservatory roof cleaning",
          "Pressure washing",
          "Roof cleaning",
          "Solar panel cleaning",
          "Other",
        ];
        return (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              Which other services do you offer? (Select all that apply)
            </h2>
            <div className="space-y-3">
              {services.map((s) => {
                const isSelected = data.otherServices.includes(s);
                return (
                  <div key={s} className="flex flex-col">
                    <Button
                      variant="outline"
                      className={`w-full justify-start h-auto py-5 px-6 text-left whitespace-normal text-base font-medium ${isSelected ? "border-primary bg-primary/5 ring-1 ring-primary text-secondary" : "text-slate-700 hover:border-primary/50"}`}
                      onClick={() => {
                        if (isSelected) {
                          updateData({
                            otherServices: data.otherServices.filter(
                              (x) => x !== s,
                            ),
                          });
                        } else {
                          updateData({
                            otherServices: [...data.otherServices, s],
                          });
                        }
                      }}
                    >
                      <div className="flex items-center w-full">
                        <div
                          className={`w-5 h-5 rounded border mr-4 flex items-center justify-center shrink-0 ${isSelected ? "bg-primary border-primary" : "border-slate-300"}`}
                        >
                          {isSelected && (
                            <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                          )}
                        </div>
                        {s}
                      </div>
                    </Button>
                    {s === "Other" && isSelected && (
                      <div className="mt-3 ml-8">
                        <Input
                          placeholder="Please specify..."
                          value={data.otherServicesText}
                          onChange={(e) =>
                            updateData({ otherServicesText: e.target.value })
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              Which town and county are you based in?
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="text-base">Town</Label>
                <Input
                  className="h-12 mt-2 text-lg"
                  value={data.town}
                  onChange={(e) => updateData({ town: e.target.value })}
                  placeholder="e.g. Brighton"
                />
              </div>
              <div>
                <Label className="text-base">County</Label>
                <Input
                  className="h-12 mt-2 text-lg"
                  value={data.county}
                  onChange={(e) => updateData({ county: e.target.value })}
                  placeholder="e.g. East Sussex"
                />
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-8 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              How many vans do you currently run?
            </h2>
            <div className="text-center py-8">
              <span className="text-6xl font-black text-primary">
                {data.vanCount}
                {data.vanCount === 10 ? "+" : ""}
              </span>
              <span className="text-xl text-slate-500 block mt-2">Vans</span>
            </div>
            <div className="px-4 pb-8">
              <Slider
                value={[data.vanCount]}
                min={1}
                max={10}
                step={1}
                onValueChange={(val) => updateData({ vanCount: val[0] })}
              />
              <div className="flex justify-between mt-2 text-sm text-slate-500 font-medium">
                <span>1</span>
                <span>10+</span>
              </div>
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-8 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              Roughly how many regular customers do you currently have?
            </h2>
            <div className="text-center py-8">
              <span className="text-6xl font-black text-primary">
                {data.customerCount}
                {data.customerCount >= 5000 ? "+" : ""}
              </span>
              <span className="text-xl text-slate-500 block mt-2">
                Customers
              </span>
            </div>
            <div className="px-4 pb-8">
              <Slider
                value={[data.customerCount]}
                min={0}
                max={5000}
                step={100}
                onValueChange={(val) => {
                  let v = val[0];
                  // Custom stepping logic
                  if (v <= 1000) {
                    v = Math.round(v / 100) * 100;
                  } else if (v <= 2000) {
                    v = Math.round(v / 250) * 250;
                  } else {
                    v = Math.round(v / 500) * 500;
                  }
                  updateData({ customerCount: v });
                }}
              />
              <div className="flex justify-between mt-2 text-sm text-slate-500 font-medium">
                <span>0</span>
                <span>5,000+</span>
              </div>
            </div>
          </div>
        );
      case 7: {
        const currentIndex = data.yearlyRoundValue
          ? Math.max(0, ROUND_VALUE_OPTIONS.indexOf(data.yearlyRoundValue))
          : 0;

        // Ensure data is set if they just click continue
        if (!data.yearlyRoundValue) {
          setTimeout(
            () => updateData({ yearlyRoundValue: ROUND_VALUE_OPTIONS[0] }),
            0,
          );
        }

        return (
          <div className="space-y-8 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              What's the approximate yearly value of your regular round?
            </h2>
            <p className="text-slate-500">
              (Just regular recurring window cleaning — exclude one-offs and
              other services)
            </p>

            <div className="text-center py-8">
              <span className="text-3xl font-bold text-primary">
                {data.yearlyRoundValue || ROUND_VALUE_OPTIONS[0]}
              </span>
            </div>

            <div className="px-4 pb-8">
              <Slider
                value={[currentIndex]}
                min={0}
                max={ROUND_VALUE_OPTIONS.length - 1}
                step={1}
                onValueChange={(val) =>
                  updateData({ yearlyRoundValue: ROUND_VALUE_OPTIONS[val[0]] })
                }
              />
              <div className="flex justify-between mt-2 text-sm text-slate-500 font-medium">
                <span>Under £30k</span>
                <span>£1m+</span>
              </div>
            </div>
          </div>
        );
      }
      case 8:
        const marketing = [
          "Nothing currently",
          "Flyers / leaflets",
          "Canvassing / door knocking",
          "Google Ads",
          "Facebook / Meta Ads",
          "Local Service Ads (Google Guaranteed)",
          "SEO",
          "Word of mouth / referrals",
          "Other",
        ];
        return (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              What marketing are you currently doing? (Select all that apply)
            </h2>
            <div className="space-y-3">
              {marketing.map((s) => {
                const isSelected = data.currentMarketing.includes(s);
                return (
                  <Button
                    key={s}
                    variant="outline"
                    className={`w-full justify-start h-auto py-5 px-6 text-left whitespace-normal text-base font-medium ${isSelected ? "border-primary bg-primary/5 ring-1 ring-primary text-secondary" : "text-slate-700 hover:border-primary/50"}`}
                    onClick={() => {
                      if (s === "Nothing currently") {
                        updateData({ currentMarketing: ["Nothing currently"] });
                      } else {
                        const newArr = data.currentMarketing.filter(
                          (x) => x !== "Nothing currently",
                        );
                        if (isSelected) {
                          updateData({
                            currentMarketing: newArr.filter((x) => x !== s),
                          });
                        } else {
                          updateData({ currentMarketing: [...newArr, s] });
                        }
                      }
                    }}
                  >
                    <div className="flex items-center w-full">
                      <div
                        className={`w-5 h-5 rounded border mr-4 flex items-center justify-center shrink-0 ${isSelected ? "bg-primary border-primary" : "border-slate-300"}`}
                      >
                        {isSelected && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                        )}
                      </div>
                      {s}
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        );
      case 9:
        return (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              Have you worked with a marketing agency or freelancer before?
            </h2>
            <div className="space-y-3">
              {[
                "Yes, with good results",
                "Yes, but results were mixed",
                "Yes, and it didn't go well",
                "No, this would be our first time",
              ].map((opt) => (
                <Button
                  key={opt}
                  variant="outline"
                  className={`w-full justify-start h-auto py-5 px-6 text-left whitespace-normal text-base font-medium ${data.agencyHistory === opt ? "border-primary bg-primary/5 ring-1 ring-primary text-secondary" : "text-slate-700 hover:border-primary/50"}`}
                  onClick={() => updateData({ agencyHistory: opt })}
                >
                  {opt}
                </Button>
              ))}
            </div>
          </div>
        );
      case 10: {
        const adSpendOptions = ["Nothing currently"];
        for (let i = 100; i <= 1000; i += 100)
          adSpendOptions.push(`£${i.toLocaleString()}`);
        for (let i = 1250; i <= 4750; i += 250)
          adSpendOptions.push(`£${i.toLocaleString()}`);
        adSpendOptions.push("£5,000+");

        const currentIndex = data.currentAdSpend
          ? Math.max(0, adSpendOptions.indexOf(data.currentAdSpend))
          : 0;

        // Ensure data is set if they just click continue
        if (!data.currentAdSpend) {
          setTimeout(
            () => updateData({ currentAdSpend: adSpendOptions[0] }),
            0,
          );
        }

        return (
          <div className="space-y-8 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              Roughly how much are you currently spending on advertising each
              month?
            </h2>
            <p className="text-slate-500">(Ad spend only — not agency fees)</p>

            <div className="text-center py-8">
              <span className="text-3xl font-bold text-primary">
                {data.currentAdSpend || adSpendOptions[0]}
              </span>
            </div>

            <div className="px-4 pb-8">
              <Slider
                value={[currentIndex]}
                min={0}
                max={adSpendOptions.length - 1}
                step={1}
                onValueChange={(val) =>
                  updateData({ currentAdSpend: adSpendOptions[val[0]] })
                }
              />
              <div className="flex justify-between mt-2 text-sm text-slate-500 font-medium">
                <span>Nothing</span>
                <span>£5k+</span>
              </div>
            </div>
          </div>
        );
      }
      case 11:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-slate-100 p-6 rounded-xl border border-slate-200 mb-6 text-slate-700 text-sm space-y-3">
              <p className="font-bold text-secondary">
                To make sure expectations are clear before we hop on a call:
              </p>
              <p>
                Our management fee starts at £749 + VAT per month for up to 7
                towns, then £70 + VAT per additional town.
              </p>
              <p>
                We require a MINIMUM ad spend of £1,000 per month on top of our
                management fee. This is the minimum needed for our system to
                generate meaningful results — anything less and we genuinely
                can't deliver the outcomes our clients expect.
              </p>
            </div>
            <h2 className="text-2xl font-bold text-secondary">
              What monthly ad budget can you commit to?
            </h2>
            <p className="text-slate-500">
              (Ad spend only — separate from our management fee)
            </p>
            <div className="space-y-3">
              {[
                "Under £1,000",
                "£1,000 – £1,500",
                "£1,500 – £2,500",
                "£2,500 – £5,000",
                "£5,000+",
              ].map((opt) => (
                <Button
                  key={opt}
                  variant="outline"
                  className={`w-full justify-start h-auto py-5 px-6 text-left whitespace-normal text-base font-medium ${data.futureAdBudget === opt ? "border-primary bg-primary/5 ring-1 ring-primary text-secondary" : "text-slate-700 hover:border-primary/50"}`}
                  onClick={() => updateData({ futureAdBudget: opt })}
                >
                  {opt}
                </Button>
              ))}
            </div>
          </div>
        );
      case 12:
        return (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-secondary">
              What's your main goal for your business over the next 12 months?
            </h2>
            <div className="space-y-3">
              {[
                "Steady — replace churn, no major growth plans",
                "Add another van or two",
                "Scale significantly (3+ vans / major growth)",
                "Build the business toward a sale or exit",
                "Not sure yet",
              ].map((opt) => (
                <Button
                  key={opt}
                  variant="outline"
                  className={`w-full justify-start h-auto py-5 px-6 text-left whitespace-normal text-base font-medium ${data.twelveMonthGoal === opt ? "border-primary bg-primary/5 ring-1 ring-primary text-secondary" : "text-slate-700 hover:border-primary/50"}`}
                  onClick={() => updateData({ twelveMonthGoal: opt })}
                >
                  {opt}
                </Button>
              ))}
            </div>
          </div>
        );
      case 13:
        return (
          <div className="space-y-8 animate-fade-in">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="text-3xl font-bold text-secondary mb-3">
                Great news!
              </h2>
              <p className="text-lg text-slate-600">
                Based on your answers, you look like a strong fit for Clean
                Marketing. Choose a time below to book your demo, and we'll see
                you there.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 space-y-4">
              <h3 className="font-bold text-secondary mb-4 border-b border-slate-200 pb-2">
                Your Answers
              </h3>
              {[
                { label: "Primary Service", val: data.primaryService, step: 1 },
                {
                  label: "Location",
                  val: `${data.town}, ${data.county}`,
                  step: 4,
                },
                { label: "Vans", val: data.vanCount.toString(), step: 5 },
                {
                  label: "Customers",
                  val: data.customerCount.toString(),
                  step: 6,
                },
                { label: "Round Value", val: data.yearlyRoundValue, step: 7 },
                {
                  label: "Current Ad Spend",
                  val: data.currentAdSpend,
                  step: 10,
                },
                { label: "Future Budget", val: data.futureAdBudget, step: 11 },
                { label: "12 Month Goal", val: data.twelveMonthGoal, step: 12 },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between items-start border-b border-slate-100 pb-3 last:border-0 last:pb-0"
                >
                  <div>
                    <p className="text-xs text-slate-500 font-medium">
                      {item.label}
                    </p>
                    <p className="text-sm font-medium text-secondary">
                      {item.val}
                    </p>
                  </div>
                  <button
                    onClick={() => setStep(item.step)}
                    className="text-xs text-primary font-medium hover:underline"
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>

            <Button
              size="lg"
              className="w-full text-lg h-14 bg-primary text-white hover:bg-[#e04825]"
              onClick={handleComplete}
            >
              Continue to Book My Demo <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-2">
      <Card className="border-slate-200 shadow-lg bg-white overflow-hidden rounded-2xl">
        <CardContent className="p-6 sm:p-10">{renderStep()}</CardContent>
      </Card>

      {step > 0 && step < 13 && (
        <div className="mt-8 flex justify-between items-center px-1">
          {step > 1 ? (
            <Button
              variant="ghost"
              onClick={prevStep}
              className="text-slate-500 hover:text-secondary text-base"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          ) : (
            <div></div>
          )}

          {(() => {
            if (step === 1 && data.primaryService) return true;
            if (
              step === 2 &&
              data.areaStatus &&
              data.areaStatus !== "Taken" &&
              data.areaStatus !== "Haven't checked"
            )
              return true;
            if (step === 3) return true;
            if (step === 4 && data.town && data.county) return true;
            if (step >= 5 && step <= 8) return true;
            if (step === 9 && data.agencyHistory) return true;
            if (step === 10 && data.currentAdSpend) return true;
            if (
              step === 11 &&
              data.futureAdBudget &&
              data.futureAdBudget !== "Under £1,000"
            )
              return true;
            if (step === 12 && data.twelveMonthGoal) return true;
            return false;
          })() && (
            <Button
              onClick={nextStep}
              size="lg"
              className="bg-secondary text-white hover:bg-secondary/90 text-base px-8 h-12"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
